import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Landmark, ShieldCheck, Lock, ArrowLeft, BadgeCheck, Send } from "lucide-react";
import { MARKETS, PRICE_LABEL, PAYMENT_URL } from "@/lib/markets";
import { buildAppxCheckoutUrl, rememberReferralCode } from "@/lib/referrals";
import BrandLogo from "@/components/BrandLogo";

const m = MARKETS.gold;

const Checkout = () => {
  const [paymentUrl, setPaymentUrl] = useState(PAYMENT_URL);

  useEffect(() => {
    const referralCode = rememberReferralCode(window.location.search);
    setPaymentUrl(buildAppxCheckoutUrl(PAYMENT_URL, referralCode));
  }, []);

  return (
    <main className="horizon-page relative min-h-screen overflow-x-hidden pt-8" data-testid="checkout-page">
      <div className="relative z-10 mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <div className="flex items-center justify-between py-4">
          <Link to="/" data-testid="checkout-back-link" className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-[#0a0a0a]">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="flex items-center gap-2.5">
            <BrandLogo className="text-lg" />
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="glass rounded-3xl p-8"
            data-testid="order-summary"
          >
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">Order Summary</p>
            <div className="mt-6 flex items-center gap-4 rounded-2xl border border-black/10 bg-black/[0.03] p-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0a0a0a]">
                <Coins className="h-7 w-7 text-[#F0C54A]" />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold uppercase text-[#0a0a0a]" data-testid="checkout-market-name">{m.name}</h2>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">{m.arena}</p>
              </div>
            </div>
            <div className="mt-6 space-y-3 border-t border-black/10 pt-6">
              {[
                [`${m.name} — 30 days access`, PRICE_LABEL.split(" ·")[0]],
                ["All 8 daily benefits", "Included"],
                ["GST & fees", "Included"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-sub font-semibold text-[#0a0a0a]">{v}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-black/10 pt-4">
                <span className="font-sub text-base font-semibold text-[#0a0a0a]">Total due today</span>
                <span className="font-display text-4xl font-extrabold text-[#0a0a0a]" data-testid="checkout-total">{PRICE_LABEL.split(" ·")[0]}</span>
              </div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400">{PRICE_LABEL} · cancel anytime</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> 256-bit encrypted</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> 30-day access</span>
              <span className="flex items-center gap-2"><BadgeCheck className="h-3.5 w-3.5" /> Cancel anytime</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="glass rounded-3xl p-8"
            data-testid="payment-panel"
          >
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">Payment Floor</p>
            <h3 className="mt-3 font-display text-2xl font-extrabold uppercase text-[#0a0a0a]">Complete your membership</h3>

            <div className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="appx-handoff-card">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0a0a0a]">
                  <Landmark className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="font-sub text-sm font-semibold text-[#0a0a0a]">OneStock Academy Secure Checkout</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">UPI · Cards · NetBanking</p>
                </div>
              </div>
              <ol className="mt-5 space-y-3 text-sm text-slate-600">
                <li className="flex gap-3"><span className="font-mono2 text-slate-400">01</span> Pay on the secure course page</li>
                <li className="flex gap-3"><span className="font-mono2 text-slate-400">02</span> You return here automatically after success</li>
                <li className="flex gap-3"><span className="font-mono2 text-slate-400">03</span> Verify your payment phone with the bot — it hands you a 5-min group link</li>
              </ol>
            </div>

            <a
              data-testid="pay-now-button"
              href={paymentUrl}
              className="btn-main mt-8 flex w-full items-center justify-center gap-3 rounded-2xl px-6 py-4 font-sub text-base font-bold uppercase tracking-wide"
            >
              <Lock className="h-5 w-5" /> Pay {PRICE_LABEL.split(" ·")[0]} — Secure Checkout
            </a>
            <p className="mt-4 text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Payment on course.onestockacademy.com · Instant unlock after
            </p>
            <Link to="/unlock" data-testid="already-paid-link" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7c3aed] via-[#5b21b6] to-[#2e1065] px-5 py-3 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_rgba(76,29,149,0.4)] transition-all hover:shadow-[0_14px_38px_rgba(76,29,149,0.55)] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2">
              <Send className="h-4 w-4" /> Already paid? Claim access
            </Link>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default Checkout;
